import React from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  useGetPositionsForIncidentId,
  useGetRosterForIncident,
} from 'hooks/incident/useRoster';
import { RosterEntry } from 'components/IAP/hooks/incidentRoster/incidentRoster.types';

type Reviewer = {
  positionName: string;
  // Users currently assigned to this position; empty when unfilled.
  users: { userGuid: string; displayName: string }[];
};

type AorReviewer = {
  reviewerItemId: string;
  displayName: string;
};

type RequestApprovalConfirmationModalProps = {
  show: boolean;
  onHide: () => void;
  onConfirm: () => void;
  isSubmitting: boolean;
  name: string;
  createdBy: string;
  createdAt: string;
  editedBy: string;
  editedAt: string;
  incidentId: string | null;
  aorReviewers?: AorReviewer[];
  isSelfReview?: boolean;
  positionNames: string[];
  fallbackReviewerLabel?: string;
  onExited?: () => void;
};

const buildReviewerList = (
  positionNames: string[],
  positions: { name: string; item_id: string }[] | undefined,
  roster: RosterEntry[] | undefined
): Reviewer[] => {
  return positionNames.map((positionName) => {
    const positionExists = positions?.some((p) => p.name === positionName);
    const users =
      (roster ?? [])
        .filter(
          (m) =>
            m.incidentPositions.some((p) => p.positionName === positionName) &&
            m.activatedTimestamp // Only include activated members
        )
        .map((m) => ({
          userGuid: m.userId,
          displayName: m.userName ?? m.emailAddress ?? m.userId,
        })) ?? [];
    return {
      positionName: positionExists
        ? positionName
        : `${positionName} (unassigned)`,
      users,
    };
  });
};

const getText = (positionName: string) => {
  if (positionName.includes('Situation Unit Leader')) {
    return 'Step 1 - Situation Unit Leader First Approval';
  }
  if (positionName.includes('Planning Section Chief')) {
    return 'Step 2 - Planning Section Chief Final Approval';
  }
  if (positionName.includes('Incident Commander')) {
    return 'Incident Commander - override authority May approve at any time';
  }
  return positionName;
};

const RequestApprovalConfirmationModal: React.FC<
  RequestApprovalConfirmationModalProps
> = ({
  show,
  onHide,
  onConfirm,
  isSubmitting,
  name,
  createdBy,
  createdAt,
  editedBy,
  editedAt,
  incidentId,
  aorReviewers,
  isSelfReview = false,
  positionNames,
  fallbackReviewerLabel,
  onExited,
}) => {
  const isAorOnly = !incidentId;
  const positionsQuery = useGetPositionsForIncidentId(incidentId ?? '');
  const rosterQuery = useGetRosterForIncident(incidentId ?? undefined);

  const reviewers = isAorOnly
    ? (aorReviewers ?? []).length > 0
      ? [
          {
            positionName: 'AOR SITREP Reviewers',
            users: (aorReviewers ?? []).map((r) => ({
              userGuid: r.reviewerItemId,
              displayName: r.displayName,
            })),
          },
        ]
      : []
    : buildReviewerList(positionNames, positionsQuery.data, rosterQuery.data);

  const isLoading =
    !!incidentId && (positionsQuery.isLoading || rosterQuery.isLoading);

  const formatTime = (iso: string) =>
    new Date(iso).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });

  return (
    <Dialog
      open={show}
      onOpenChange={(open) => {
        if (!open) onHide();
      }}
      onOpenChangeComplete={(open) => {
        if (!open) onExited?.();
      }}
    >
      <DialogContent className="tw:sm:max-w-3xl tw:max-h-[85vh] tw:overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Send draft {name} for review</DialogTitle>
          <DialogDescription>
            Created by {createdBy} at {formatTime(createdAt)}. <br />
            Last edited by {editedBy} at {formatTime(editedAt)}.
          </DialogDescription>
        </DialogHeader>
        <div>
          <div className="rounded border p-1">
            {isAorOnly ? (
              <>
                <div>Approval Workflow for AOR SITREPs</div>
                <div className="mt-2">
                  {isSelfReview
                    ? 'You are the only eligible reviewer for this report. You will be able to review and approve this draft yourself.'
                    : `Any user with permission to approve SITREPs for ${name} can approve this draft.`}
                </div>
              </>
            ) : (
              <>
                <div>Approval Workflow for Incident SITREPs</div>
                <ol className="mt-2">
                  <li>
                    Send first to the <b>Situation Unit Leader</b> for review.
                  </li>
                  <li className="mt-2">
                    Then routed to the <b>Planning Section Chief</b> for review
                    and approval.
                  </li>
                </ol>
                <div className="mt-2">
                  The <b>Incident Commander</b> can review and approve this
                  draft at any point, regardless of where it is in the workflow
                  above or the actions of the other reviewers.
                </div>
              </>
            )}
          </div>
          <div className="mt-2 rounded border p-1">
            <div>Editing Locked after Submission</div>
            <div className="mt-2">
              {isSelfReview ? (
                <>
                  Once this draft is submitted for review, it can only be edited
                  from the Review tab, where you can approve or reject it.
                </>
              ) : (
                <>
                  Once this draft is submitted for review, only the listed
                  reviewers
                  {isAorOnly
                    ? ` for ${name} `
                    : ' (Situation Unit Leader, Planning Section Chief, and Incident Commander) '}
                  will be able to edit it. You will not be able to edit the
                  draft until a reviewer returns it.
                </>
              )}
            </div>
          </div>
        </div>
        <div className="mt-2">Notified Reviewers</div>
        {reviewers.length === 0 ? (
          <p className="fst-italic">
            {fallbackReviewerLabel ??
              'No reviewers are configured for this report.'}
          </p>
        ) : (
          <div className="mt-2">
            {reviewers.map((reviewer) => (
              <div
                key={reviewer.positionName}
                className="mb-2 rounded border p-2"
              >
                <div>{getText(reviewer.positionName)}</div>
                <hr className="my-2" />
                {reviewer.users.length > 0 ? (
                  <div className="mb-0">
                    {reviewer.users.map((user, i) => (
                      <React.Fragment key={user.userGuid}>
                        {i > 0 && <hr className="my-1" />}
                        <div>
                          {isAorOnly
                            ? user.displayName
                            : reviewer.positionName + ' ' + user.displayName}
                        </div>
                      </React.Fragment>
                    ))}
                  </div>
                ) : (
                  <div className="fst-italic text-muted">
                    No user currently assigned
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={onHide}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={onConfirm}
            disabled={isSubmitting || isLoading}
          >
            {isSubmitting ? 'Sending' : 'Send for Review'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default RequestApprovalConfirmationModal;
